import {
  Background,
  BackgroundVariant,
  Controls,
  Handle,
  MiniMap,
  Position,
  ReactFlow,
  addEdge,
  useEdgesState,
  useNodesState,
  type Connection,
  type Edge,
  type Node,
  type NodeProps,
  type OnConnectEnd,
  type ReactFlowInstance,
  type Viewport,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  SOLUTION_CANVAS_CATALOG,
  emptySolutionCanvasDraft,
  type SolutionCanvasDraftV1,
  type SolutionCanvasIssue,
  type SolutionCanvasNodeType,
} from "@clickdialog/solution-contract";
import {
  Bot,
  BookOpen,
  CalendarDays,
  Copy,
  Headphones,
  MessageCircle,
  Phone,
  Plus,
  Save,
  Send,
  Trash2,
  UserRound,
  Workflow,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  PlatformApiError,
  type PlatformApiClient,
  type SolutionFactoryCanvas,
  type SolutionFactoryDraft,
} from "../api";

type CanvasNodeData = {
  nodeType: SolutionCanvasNodeType;
  label: string;
};

type CanvasFlowNode = Node<CanvasNodeData, "solution">;

const NODE_LABELS: Record<SolutionCanvasNodeType, string> = {
  "channel.voice": "Voice",
  "channel.whatsapp": "WhatsApp",
  "context.conversation": "Conversation",
  "ai.agent": "AI Agent",
  "ai.knowledge": "Knowledge",
  "business.customer": "Customer",
  "business.scheduling": "Scheduling",
  "flow.automation": "Automation",
  "control.human": "Human",
};

const NODE_CATEGORY: Record<SolutionCanvasNodeType, string> = {
  "channel.voice": "CHANNELS",
  "channel.whatsapp": "CHANNELS",
  "context.conversation": "CONTEXT",
  "ai.agent": "AI",
  "ai.knowledge": "AI",
  "business.customer": "BUSINESS",
  "business.scheduling": "BUSINESS",
  "flow.automation": "FLOW",
  "control.human": "CONTROL",
};

const NODE_ICON: Record<
  SolutionCanvasNodeType,
  React.ComponentType<{ size?: number; strokeWidth?: number }>
> = {
  "channel.voice": Phone,
  "channel.whatsapp": Send,
  "context.conversation": MessageCircle,
  "ai.agent": Bot,
  "ai.knowledge": BookOpen,
  "business.customer": UserRound,
  "business.scheduling": CalendarDays,
  "flow.automation": Workflow,
  "control.human": Headphones,
};

function SolutionFlowNode({ data, selected }: NodeProps<CanvasFlowNode>) {
  const Icon = NODE_ICON[data.nodeType];
  const catalog = SOLUTION_CANVAS_CATALOG[data.nodeType];
  return <div
    className="freeform-node"
    data-selected={selected ? "true" : "false"}
    data-kind={catalog.kind}
  >
    <Handle
      className="freeform-handle freeform-handle-target"
      type="target"
      position={Position.Left}
    />
    <div className="freeform-node-icon" aria-hidden="true">
      <Icon size={17} strokeWidth={1.8} />
    </div>
    <div className="freeform-node-copy">
      <small>{NODE_CATEGORY[data.nodeType]}</small>
      <strong>{data.label}</strong>
      <span>{catalog.description}</span>
    </div>
    <Handle
      className="freeform-handle freeform-handle-source"
      type="source"
      position={Position.Right}
    />
  </div>;
}

const NODE_TYPES = { solution: SolutionFlowNode } as const;

function nodeId(): string {
  return "node-" + crypto.randomUUID().replaceAll("-", "").slice(0, 20);
}

function edgeId(): string {
  return "edge-" + crypto.randomUUID().replaceAll("-", "").slice(0, 20);
}

function flowNodes(canvas: SolutionCanvasDraftV1): CanvasFlowNode[] {
  return canvas.nodes.map((node) => ({
    id: node.id,
    type: "solution",
    position: { x: node.position.x, y: node.position.y },
    data: {
      nodeType: node.type,
      label: node.label,
    },
  }));
}

function flowEdges(canvas: SolutionCanvasDraftV1): Edge[] {
  return canvas.edges.map((edge) => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    type: "smoothstep",
  }));
}

function errorMessage(cause: unknown, fallback: string): string {
  return cause instanceof PlatformApiError ? cause.message : fallback;
}

export function FreeformSolutionCanvas({
  api,
  onCompiledDraft,
}: {
  api?: PlatformApiClient;
  onCompiledDraft: (draft: SolutionFactoryDraft) => void;
}) {
  const [record, setRecord] = useState<SolutionFactoryCanvas | null>(null);
  const [records, setRecords] = useState<readonly SolutionFactoryCanvas[]>([]);
  const [name, setName] = useState("Nueva solución");
  const [description, setDescription] = useState("");
  const [nodes, setNodes, onNodesChange] = useNodesState<CanvasFlowNode>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [viewport, setViewportState] = useState<Viewport>({ x: 0, y: 0, zoom: 1 });
  const [instance, setInstance] =
    useState<ReactFlowInstance<CanvasFlowNode, Edge> | null>(null);
  const flowSurfaceRef = useRef<HTMLElement | null>(null);
  const [libraryOpen, setLibraryOpen] = useState(true);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [quickConnect, setQuickConnect] = useState<{
    sourceId: string;
    position: { x: number; y: number };
  } | null>(null);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(Boolean(api));
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [issues, setIssues] = useState<readonly SolutionCanvasIssue[]>([]);
  const [error, setError] = useState<string | null>(null);

  const selectedNode = selectedId
    ? nodes.find((node) => node.id === selectedId) ?? null
    : null;

  const catalogTypes = useMemo(() => {
    const search = query.trim().toLowerCase();
    return (Object.keys(SOLUTION_CANVAS_CATALOG) as SolutionCanvasNodeType[])
      .filter((type) => {
        if (!search) return true;
        const entry = SOLUTION_CANVAS_CATALOG[type];
        return [
          NODE_LABELS[type],
          NODE_CATEGORY[type],
          entry.description,
          entry.kind,
          entry.capabilityKey ?? "",
          entry.channelKey ?? "",
        ].some((value) => value.toLowerCase().includes(search));
      });
  }, [query]);

  const categories = useMemo(
    () => [...new Set(catalogTypes.map((type) => NODE_CATEGORY[type]))],
    [catalogTypes],
  );

  function applyCanvas(canvas: SolutionCanvasDraftV1, nextRecord: SolutionFactoryCanvas | null) {
    setRecord(nextRecord);
    setName(canvas.name);
    setDescription(canvas.description);
    setNodes(flowNodes(canvas));
    setEdges(flowEdges(canvas));
    setViewportState(canvas.viewport);
    setSelectedId(null);
    setQuickConnect(null);
    setIssues([]);
    setDirty(false);
    window.setTimeout(() => {
      void instance?.setViewport(canvas.viewport, { duration: 180 });
    }, 0);
  }

  useEffect(() => {
    if (!api) {
      applyCanvas(emptySolutionCanvasDraft(), null);
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    api.solutionFactoryCanvases(100)
      .then(({ canvases }) => {
        if (!active) return;
        setRecords(canvases);
        const latest = canvases[0];
        if (latest) applyCanvas(latest.canvas, latest);
        else applyCanvas(emptySolutionCanvasDraft(), null);
      })
      .catch((cause) => {
        if (!active) return;
        setError(errorMessage(cause, "No se pudieron cargar las soluciones."));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  // instance intentionally excluded: loading persistence must run once per API binding.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  useEffect(() => {
    if (!instance) return;
    void instance.setViewport(viewport, { duration: 0 });
  }, [instance]);

  function currentCanvas(): SolutionCanvasDraftV1 {
    return {
      schemaVersion: 1,
      name: name.trim() || "Nueva solución",
      description,
      nodes: nodes.map((node) => ({
        id: node.id,
        type: node.data.nodeType,
        label: node.data.label,
        position: {
          x: node.position.x,
          y: node.position.y,
        },
      })),
      edges: edges.map((edge) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
      })),
      viewport,
    };
  }

  function startNewSolution() {
    applyCanvas(emptySolutionCanvasDraft(), null);
    setNotice("Canvas vacío listo para construir.");
    setError(null);
    setLibraryOpen(true);
  }

  function openRecord(next: SolutionFactoryCanvas) {
    applyCanvas(next.canvas, next);
    setNotice(`Solución abierta · v${next.version}.`);
    setError(null);
  }

  function addNode(type: SolutionCanvasNodeType, position?: { x: number; y: number }) {
    const rect = flowSurfaceRef.current?.getBoundingClientRect();
    const surfaceLeft = rect?.left ?? 0;
    const surfaceRight = rect?.right ?? window.innerWidth;
    const surfaceTop = rect?.top ?? 0;
    const surfaceBottom = rect?.bottom ?? window.innerHeight;
    const leftOverlay = libraryOpen ? 306 : 72;
    const rightOverlay = selectedNode ? 336 : 28;
    const safeLeft = surfaceLeft + leftOverlay + 32;
    const safeRight = Math.max(safeLeft + 220, surfaceRight - rightOverlay - 32);
    const safeTop = surfaceTop + 110;
    const safeBottom = Math.max(safeTop + 160, surfaceBottom - 150);
    const columns = Math.max(1, Math.floor((safeRight - safeLeft) / 230));
    const column = nodes.length % columns;
    const row = Math.floor(nodes.length / columns);
    const screenPosition = {
      x: Math.min(safeRight, safeLeft + 45 + column * 230),
      y: Math.min(safeBottom, safeTop + row * 145),
    };
    const fallback = instance?.screenToFlowPosition(screenPosition) ?? {
      x: column * 250,
      y: row * 160,
    };
    const id = nodeId();
    const next: CanvasFlowNode = {
      id,
      type: "solution",
      position: position ?? quickConnect?.position ?? fallback,
      data: {
        nodeType: type,
        label: NODE_LABELS[type],
      },
    };
    setNodes((current) => [...current, next]);
    if (quickConnect) {
      const sourceId = quickConnect.sourceId;
      setEdges((current) => addEdge({
        id: edgeId(),
        source: sourceId,
        target: id,
        type: "smoothstep",
      }, current));
    }
    setQuickConnect(null);
    setSelectedId(id);
    setDirty(true);
    setIssues([]);
    setNotice(null);
    setError(null);
  }

  const connectEnd: OnConnectEnd = (event, state) => {
    if (
      state.isValid ||
      !state.fromNode ||
      state.fromHandle?.type !== "source" ||
      !instance
    ) return;
    const point = "changedTouches" in event ? event.changedTouches[0] : event;
    if (!point) return;
    const target = event.target;
    if (
      target instanceof Element &&
      target.closest(".react-flow__node")
    ) return;
    setQuickConnect({
      sourceId: state.fromNode.id,
      position: instance.screenToFlowPosition({
        x: point.clientX,
        y: point.clientY,
      }),
    });
    setLibraryOpen(true);
    setQuery("");
    setNotice("Add next component · el próximo bloque quedará conectado automáticamente.");
  };

  function deleteSelected() {
    if (!selectedId) return;
    setNodes((current) => current.filter((node) => node.id !== selectedId));
    setEdges((current) =>
      current.filter(
        (edge) => edge.source !== selectedId && edge.target !== selectedId,
      ),
    );
    setSelectedId(null);
    setDirty(true);
    setIssues([]);
  }

  function duplicateSelected() {
    if (!selectedNode) return;
    const id = nodeId();
    setNodes((current) => [
      ...current,
      {
        ...selectedNode,
        id,
        selected: false,
        position: {
          x: selectedNode.position.x + 48,
          y: selectedNode.position.y + 48,
        },
        data: { ...selectedNode.data },
      },
    ]);
    setSelectedId(id);
    setDirty(true);
    setIssues([]);
  }

  function renameSelected(value: string) {
    if (!selectedId) return;
    setNodes((current) => current.map((node) =>
      node.id === selectedId
        ? { ...node, data: { ...node.data, label: value } }
        : node
    ));
    setDirty(true);
    setIssues([]);
  }

  function connect(connection: Connection) {
    if (!connection.source || !connection.target) return;
    if (connection.source === connection.target) return;
    const duplicate = edges.some(
      (edge) =>
        edge.source === connection.source &&
        edge.target === connection.target,
    );
    if (duplicate) return;
    setEdges((current) =>
      addEdge(
        {
          ...connection,
          id: edgeId(),
          type: "smoothstep",
        },
        current,
      )
    );
    setDirty(true);
    setIssues([]);
  }

  async function persist(): Promise<SolutionFactoryCanvas | null> {
    if (!api) {
      setError("La persistencia Solution Factory no está conectada.");
      return null;
    }
    const canvas = currentCanvas();
    const result = record
      ? await api.updateSolutionFactoryCanvas(record.id, canvas, record.version)
      : await api.createSolutionFactoryCanvas(canvas);
    const saved = result.canvas;
    setRecord(saved);
    setRecords((current) => [
      saved,
      ...current.filter((candidate) => candidate.id !== saved.id),
    ].slice(0, 100));
    setDirty(false);
    setNotice(`Canvas guardado · v${saved.version}.`);
    return saved;
  }

  async function save() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await persist();
    } catch (cause) {
      setError(
        cause instanceof PlatformApiError && cause.code === "version_conflict"
          ? "Esta solución cambió en otra sesión. Volvé a abrirla antes de guardar."
          : errorMessage(cause, "No se pudo guardar la solución."),
      );
    } finally {
      setBusy(false);
    }
  }

  async function compile() {
    if (!api) {
      setError("La persistencia Solution Factory no está conectada.");
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    setIssues([]);
    try {
      const saved = dirty || !record ? await persist() : record;
      if (!saved) return;
      const templateKey =
        "custom-" +
        saved.id.slice("cd_sfcv_".length, "cd_sfcv_".length + 24);
      const result = await api.compileSolutionFactoryCanvas(
        saved.id,
        saved.version,
        templateKey,
      );
      if (!result.compiled) {
        setIssues(result.issues);
        setNotice("La solución todavía está incompleta. Corregí los puntos marcados.");
        return;
      }
      onCompiledDraft(result.draft);
      setNotice(`Blueprint compilado · Draft v${result.draft.version} listo para Candidate/Demo.`);
    } catch (cause) {
      setError(errorMessage(cause, "No se pudo compilar la solución."));
    } finally {
      setBusy(false);
    }
  }

  return <div className="freeform-builder">
    <div className="freeform-builder-toolbar">
      <div className="freeform-toolbar-left">
        <button type="button" className="freeform-new-button" onClick={startNewSolution}>
          <Plus size={15} /> New Solution
        </button>
        <select
          aria-label="Solución guardada"
          value={record?.id ?? ""}
          onChange={(event) => {
            const next = records.find((candidate) => candidate.id === event.target.value);
            if (next) openRecord(next);
          }}
        >
          <option value="">{record ? record.name : "Canvas sin guardar"}</option>
          {records.map((candidate) =>
            <option key={candidate.id} value={candidate.id}>
              {candidate.name} · v{candidate.version}
            </option>)}
        </select>
        <span className="freeform-save-state" data-dirty={dirty ? "true" : "false"}>
          {loading ? "Cargando…" : record ? `v${record.version} · ${dirty ? "sin guardar" : "guardado"}` : "nuevo"}
        </span>
      </div>
      <div className="freeform-toolbar-right">
        <button
          type="button"
          className="freeform-secondary-action"
          disabled={busy}
          onClick={() => void save()}
        >
          <Save size={14} /> Save
        </button>
        <button
          type="button"
          className="freeform-primary-action"
          disabled={busy}
          onClick={() => void compile()}
        >
          {busy ? "Procesando…" : "Validate & Compile"}
        </button>
      </div>
    </div>

    <div className="freeform-builder-body">
      <aside className="freeform-library" data-open={libraryOpen ? "true" : "false"}>
        <div className="freeform-library-heading">
          <div>
            <small>COMPONENT LIBRARY</small>
            <strong>Build your solution</strong>
          </div>
          <button
            type="button"
            aria-label={libraryOpen ? "Cerrar biblioteca" : "Abrir biblioteca"}
            onClick={() => setLibraryOpen((value) => !value)}
          >
            {libraryOpen ? <X size={14} /> : <Plus size={14} />}
          </button>
        </div>
        {libraryOpen && <>
          {quickConnect && <div className="freeform-quick-connect">
            <div>
              <small>ADD NEXT COMPONENT</small>
              <strong>Elegí el próximo paso</strong>
            </div>
            <button
              type="button"
              onClick={() => {
                setQuickConnect(null);
                setNotice(null);
              }}
            ><X size={12} /> Cancel</button>
          </div>}
          <input
            aria-label="Buscar componente"
            value={query}
            placeholder="Search components…"
            onChange={(event) => setQuery(event.target.value)}
          />
          <div className="freeform-library-scroll">
            {categories.map((category) => <div key={category} className="freeform-library-group">
              <span>{category}</span>
              {catalogTypes
                .filter((type) => NODE_CATEGORY[type] === category)
                .map((type) => {
                  const Icon = NODE_ICON[type];
                  return <button
                    type="button"
                    key={type}
                    draggable
                    onDragStart={(event) => {
                      event.dataTransfer.effectAllowed = "copy";
                      event.dataTransfer.setData("application/clickdialog-node-type", type);
                    }}
                    onClick={() => addNode(type)}
                  >
                    <span><Icon size={15} /></span>
                    <div>
                      <strong>{NODE_LABELS[type]}</strong>
                      <small>{SOLUTION_CANVAS_CATALOG[type].description}</small>
                    </div>
                    <Plus size={13} />
                  </button>;
                })}
            </div>)}
          </div>
        </>}
      </aside>

      <main
        ref={flowSurfaceRef}
        className="freeform-flow-surface"
        onDragOver={(event) => {
          event.preventDefault();
          event.dataTransfer.dropEffect = "copy";
        }}
        onDrop={(event) => {
          event.preventDefault();
          const type = event.dataTransfer.getData(
            "application/clickdialog-node-type",
          ) as SolutionCanvasNodeType;
          if (!(type in SOLUTION_CANVAS_CATALOG) || !instance) return;
          addNode(type, instance.screenToFlowPosition({
            x: event.clientX,
            y: event.clientY,
          }));
        }}
      >
        <ReactFlow<CanvasFlowNode, Edge>
          nodes={nodes}
          edges={edges}
          nodeTypes={NODE_TYPES}
          onNodesChange={(changes) => {
            onNodesChange(changes);
            if (changes.some((change) =>
              ["position", "remove", "add", "replace"].includes(change.type)
            )) setDirty(true);
          }}
          onEdgesChange={(changes) => {
            onEdgesChange(changes);
            if (changes.some((change) => change.type !== "select")) setDirty(true);
          }}
          onConnect={connect}
          onConnectEnd={connectEnd}
          onInit={setInstance}
          onNodeClick={(_, node) => setSelectedId(node.id)}
          onPaneClick={() => setSelectedId(null)}
          onMoveEnd={(_, nextViewport) => {
            const changed =
              Math.abs(nextViewport.x - viewport.x) > 0.01 ||
              Math.abs(nextViewport.y - viewport.y) > 0.01 ||
              Math.abs(nextViewport.zoom - viewport.zoom) > 0.001;
            setViewportState(nextViewport);
            if (changed) setDirty(true);
          }}
          defaultViewport={viewport}
          minZoom={0.15}
          maxZoom={2.5}
          panOnScroll
          selectionOnDrag
          multiSelectionKeyCode="Shift"
          deleteKeyCode={["Backspace", "Delete"]}
          proOptions={{ hideAttribution: true }}
        >
          <Background
            variant={BackgroundVariant.Dots}
            gap={24}
            size={1.3}
          />
          <Controls position="bottom-left" />
          <MiniMap
            position="bottom-right"
            pannable
            zoomable
            nodeStrokeWidth={2}
          />
        </ReactFlow>

        {nodes.length === 0 && <button
          type="button"
          className="freeform-empty-state"
          onClick={() => setLibraryOpen(true)}
        >
          <span><Plus size={20} /></span>
          <strong>Add first component</strong>
          <small>Drag a component here or click to open the library.</small>
        </button>}

        <div className="freeform-canvas-status">
          <span>{nodes.length} nodes</span>
          <span>{edges.length} connections</span>
          <span>{Math.round(viewport.zoom * 100)}%</span>
        </div>
      </main>

      {selectedNode && <aside className="freeform-node-inspector">
        <div className="freeform-inspector-heading">
          <div>
            <small>SELECTED NODE</small>
            <strong>{NODE_LABELS[selectedNode.data.nodeType]}</strong>
          </div>
          <button
            type="button"
            aria-label="Cerrar inspector de nodo"
            onClick={() => setSelectedId(null)}
          >
            <X size={14} />
          </button>
        </div>
        <label>
          Name
          <input
            value={selectedNode.data.label}
            onChange={(event) => renameSelected(event.target.value)}
          />
        </label>
        <dl>
          <div>
            <dt>Type</dt>
            <dd>{selectedNode.data.nodeType}</dd>
          </div>
          <div>
            <dt>Kind</dt>
            <dd>{SOLUTION_CANVAS_CATALOG[selectedNode.data.nodeType].kind}</dd>
          </div>
          {SOLUTION_CANVAS_CATALOG[selectedNode.data.nodeType].capabilityKey && <div>
            <dt>Capability</dt>
            <dd>{SOLUTION_CANVAS_CATALOG[selectedNode.data.nodeType].capabilityKey}</dd>
          </div>}
          {SOLUTION_CANVAS_CATALOG[selectedNode.data.nodeType].channelKey && <div>
            <dt>Channel</dt>
            <dd>{SOLUTION_CANVAS_CATALOG[selectedNode.data.nodeType].channelKey}</dd>
          </div>}
        </dl>
        <div className="freeform-inspector-actions">
          <button type="button" onClick={duplicateSelected}>
            <Copy size={13} /> Duplicate
          </button>
          <button type="button" className="danger" onClick={deleteSelected}>
            <Trash2 size={13} /> Delete
          </button>
        </div>
      </aside>}

      <aside className="freeform-solution-meta">
        <label>
          Solution name
          <input
            value={name}
            maxLength={120}
            onChange={(event) => {
              setName(event.target.value);
              setDirty(true);
              setIssues([]);
            }}
          />
        </label>
        <label>
          Description
          <textarea
            rows={2}
            maxLength={500}
            value={description}
            onChange={(event) => {
              setDescription(event.target.value);
              setDirty(true);
            }}
            placeholder="What does this solution do?"
          />
        </label>
        {issues.length > 0 && <div className="freeform-validation-issues" role="status">
          <strong>Before this can become a Blueprint</strong>
          {issues.slice(0, 6).map((issue, index) =>
            <p key={issue.code + issue.path + index}>
              <span>{issue.code}</span>
              {issue.message}
            </p>)}
        </div>}
        {notice && <p className="freeform-feedback success">{notice}</p>}
        {error && <p className="freeform-feedback error" role="alert">{error}</p>}
      </aside>
    </div>
  </div>;
}
